/**
 * Parses the structured /restaurant/customer/cart/set 400 validator error
 * shipped by the Eatzy API team to replace the generic "Cart array not valid"
 * response (see API task that pairs with consumer Task #139).
 *
 * The backend may put the structured details either at the top level of
 * `err.data` or inside a `errors[]` array. Both shapes are accepted so that
 * minor schema variations between staging/prod don't silently fall back to
 * the unfriendly generic toast.
 *
 * Expected backend payload (best-effort — we read every known alias):
 *
 *   {
 *     "message": "Cart array not valid",
 *     "errors": [
 *       {
 *         "reason_code": "ITEM_UNAVAILABLE",
 *         "field":       "cart[0].items[1].quantity",
 *         "menu_item_id":   12345,
 *         "menu_item_name": "Cheeseburger",
 *         "cart_sort":      2,
 *         "detail":         "Item is no longer on the menu"
 *       }
 *     ]
 *   }
 *
 * Returns `null` when the error does not look like a structured validator
 * rejection (so the caller falls back to legacy handling). Otherwise returns:
 *
 *   {
 *     friendlyMessage: string,   // safe to show in a toast
 *     reasonCode:      string,   // canonical reason code (UPPER_SNAKE)
 *     field:           string|null,
 *     itemId:          number|null,
 *     itemName:        string|null,
 *     sort:            number|null,
 *     detail:          string|null,
 *     raw:             object,   // the structured payload echoed for Bugsnag
 *   }
 */

const FRIENDLY_MESSAGES = {
  ITEM_UNAVAILABLE: (name) =>
    name
      ? `${name} is no longer available.`
      : 'An item in your cart is no longer available.',
  MENU_ITEM_NOT_FOUND: (name) =>
    name
      ? `${name} is no longer on the menu.`
      : 'An item in your cart is no longer on the menu.',
  OUT_OF_STOCK: (name) =>
    name ? `${name} is out of stock.` : 'An item in your cart is out of stock.',
  INVALID_QUANTITY: (name) =>
    name
      ? `Quantity for ${name} is invalid. Please update it and try again.`
      : 'A quantity in your cart is invalid. Please update it and try again.',
  QUANTITY_OUT_OF_RANGE: (name) =>
    name
      ? `Quantity for ${name} is out of the allowed range.`
      : 'A quantity in your cart is out of the allowed range.',
  MODIFIER_REQUIRED: (name) =>
    name
      ? `${name} is missing a required option. Please choose one.`
      : 'An item in your cart is missing a required option.',
  MISSING_REQUIRED_MODIFIER: (name) =>
    name
      ? `${name} is missing a required option. Please choose one.`
      : 'An item in your cart is missing a required option.',
  MODIFIER_INVALID: (name) =>
    name
      ? `One of the options for ${name} is no longer available.`
      : 'One of the options for an item in your cart is no longer available.',
  INVALID_MODIFIER: (name) =>
    name
      ? `One of the options for ${name} is no longer available.`
      : 'One of the options for an item in your cart is no longer available.',
  PRICE_MISMATCH: (name) =>
    name
      ? `The price of ${name} has changed. Please review your cart.`
      : 'A price in your cart has changed. Please review your cart.',
};

const STRUCTURED_LIKE_KEYS = [
  'reason_code',
  'reasonCode',
  'errors',
  'menu_item_id',
  'menuItemId',
  'cart_sort',
  'cartSort',
];

const looksStructured = (data) => {
  if (!data || typeof data !== 'object') return false;
  return STRUCTURED_LIKE_KEYS.some((k) => k in data);
};

const pick = (obj, ...keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj?.[keys[i]];
    if (v !== undefined && v !== null) return v;
  }
  return null;
};

const numericOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function parseCartValidatorError(err) {
  const data = err?.data;
  if (!data || typeof data !== 'object') return null;

  const message = typeof data.message === 'string' ? data.message : '';
  const isCartValidatorMessage = /cart array not valid/i.test(message);

  if (!isCartValidatorMessage && !looksStructured(data)) return null;

  const detailObj =
    (Array.isArray(data.errors) && data.errors[0]) ||
    (Array.isArray(data.error) && data.error[0]) ||
    data;

  if (!detailObj || typeof detailObj !== 'object') return null;

  const rawReason =
    pick(detailObj, 'reason_code', 'reasonCode', 'code', 'reason') || '';
  const reasonCode = String(rawReason).toUpperCase();
  const field = pick(detailObj, 'field', 'path');
  const itemId = numericOrNull(
    pick(detailObj, 'menu_item_id', 'menuItemId', 'item_id', 'itemId')
  );
  const itemName = pick(
    detailObj,
    'menu_item_name',
    'menuItemName',
    'item_name',
    'itemName',
    'name'
  );
  const sort = numericOrNull(
    pick(detailObj, 'cart_sort', 'cartSort', 'sort', 'index')
  );
  // Note: we deliberately do NOT fall back to `data.message` here. When the
  // detailObj is the top-level data (no `errors[]` wrapper), `data.message`
  // is the generic wrapper string ("Cart array not valid") and is not a
  // per-item detail. Pulling it in would make the legacy unstructured 400
  // look structured and break the backward-compat fallback path.
  const detail = pick(detailObj, 'detail', 'description');

  // Require *some* structure beyond the generic message. If we have neither a
  // reason code nor any item identifier, the backend hasn't actually shipped
  // structured details yet — fall back to legacy handling.
  if (!reasonCode && itemId == null && !field && !detail) return null;

  // Per task #139 spec: only known reason codes get the structured friendly
  // toast + distinct Bugsnag class. For unmapped reasons we return null so the
  // saga falls through to the existing ITEM_NOT_AVAILABLE / raw-message
  // behaviour. This keeps the customer-facing UX deterministic and lets
  // Bugsnag's existing grouping highlight new reason codes naturally.
  const builder = FRIENDLY_MESSAGES[reasonCode];
  if (!builder) return null;
  const friendlyMessage = builder(itemName || null);

  return {
    friendlyMessage,
    reasonCode: reasonCode || null,
    field: field || null,
    itemId,
    itemName: itemName || null,
    sort,
    detail: typeof detail === 'string' ? detail : null,
    raw: data,
  };
}
